import Select from '@/components/Select.jsx';
import ArgumentLabel from '@/components/arguments/ArgumentLabel.jsx';
import ArgumentWrapper from '@/components/arguments/ArgumentWrapper.jsx';
import Panel from '@/components/panel/Panel.jsx';
import RightPaneFieldV2 from '@/components/v2/RightPaneFieldV2.jsx';
import getOrdinalName from '@/lib/getOrdinalName.js';
import selectors from '@/redux/selectors/selectors.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { appSchema } from '@shared/schema/appSchema.js';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { useSelector } from 'react-redux';

const ConditionalAuthSection = ({ disabled = false }) => {
  const credentials = useSelector(selectors.credentials);
  const stepAppId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.appId);
  const conditionalAuthItems = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.options?.conditionalAuth) || [];
  const operatorOptions = appSchema?.api?.filters?.operators?.map((n) => ({ ...n, label: n.shorthand }));
  const credentialOptions = credentials
    ?.filter((n) => n.appId === stepAppId)
    .sort(sortByProp(['valid', 'updatedOn'], ['desc', 'desc']))
    .map((n) => {
      const steps = Number(n?.workflowStepCount || 0);
      return {
        label: (
          <>
            <span>{n.meta?.label || 'Unnamed'}</span>
            <span className="tag">{`${steps} workflow${steps === 1 ? '' : 's'}`}</span>
          </>
        ),
        selectedLabel: n.meta?.label || 'Unnamed',
        value: n.credentialId
      };
    });

  const isIncludes = (option) => option?.operator === 'includes';
  const isSingleField = (option) => ['includes', 'exist', 'nexist', 'empty', 'nempty'].includes(option?.operator);

  const handleChange = ({ id, key, value }) => {
    stepActions.set(`options.conditionalAuth[id:${id}]`, (prev) => {
      const newValue = typeof value !== 'object' ? value : { ...prev[key], ...value };
      return { ...prev, [key]: newValue };
    });
  };

  const handleDelete = (setting) => {
    if (!setting) {
      return;
    }
    stepActions.set(`options.conditionalAuth`, (n = []) => n?.filter((m) => m?.id !== setting?.id));
  };

  if (!conditionalAuthItems?.length) {
    return null;
  }

  return (
    <>
      {conditionalAuthItems?.map((option, idx) => (
        <Panel.Section className={'simple-arguments'} title={`${getOrdinalName(idx + 1)} Auth Condition`} onDelete={() => handleDelete(option)} key={idx}>
          <ArgumentWrapper>
            <ArgumentLabel className={'small-gap'} label={'Field'} description={'The field to filter on.'} />
            <RightPaneFieldV2 disabled={disabled} placeholder={'Field'} value={option?.field} type={'lexical'} onChange={(value) => handleChange({ id: option?.id, key: 'field', value })} />
          </ArgumentWrapper>
          <ArgumentWrapper>
            <ArgumentLabel className={'small-gap'} label={'Operator'} description={'The operator to filter on.'} />
            <Select disabled={disabled} options={operatorOptions} value={operatorOptions?.find((n) => n?.value === option?.operator)?.label} onChange={({ value }) => handleChange({ id: option?.id, key: 'operator', value })} operator={true} />
          </ArgumentWrapper>
          {!isSingleField(option) && (
            <ArgumentWrapper>
              <ArgumentLabel className={'small-gap'} label={isIncludes(option) ? 'Values' : 'Value'} description={isIncludes(option) ? 'The values to filter on.' : 'The value to filter on.'} />
              <RightPaneFieldV2 disabled={disabled} placeholder={isIncludes(option) ? 'Values' : 'Value'} value={option?.value} type={'lexical'} onChange={(value) => handleChange({ id: option?.id, key: 'value', value })} />
            </ArgumentWrapper>
          )}
          <ArgumentWrapper>
            <ArgumentLabel className={'small-gap'} label={'Connection'} description={'Which connection to use for this condition.'} />
            <Select disabled={disabled} options={credentialOptions} placeholder={'Connection'} value={credentialOptions?.find((n) => n?.value === option?.credentialId)?.selectedLabel} onChange={({ value }) => handleChange({ id: option?.id, key: 'credentialId', value })} />
          </ArgumentWrapper>
        </Panel.Section>
      ))}
    </>
  );
};

export default ConditionalAuthSection;
